<template lang="html">
    <report-section
        v-if="reportData.hydrologicVariability"
        :id="id"
        title="Hydrologic Variability"
        class="page-break-after"
    >
        <p>
            The potential variability of flows in the query basin has been estimated by comparing its physical and
            environmental characteristics to other watersheds which have hydrometric monitoring records. A similarity
            score was used to quantify the basin comparisons using multiple physical and environmental metrics<sup>
                <footnote-tooltip
                    :footnote-number="13"
                />
            </sup>. The statistical distribution of streamflows for each month from the monitored watersheds, was then
            used to estimate a potential range of flows for the query basin. The physical and hydroclimatic
            characteristics and comparisons are based on those used for hierarchical clustering of river ecosystems in
            BC<sup>
                <footnote-tooltip
                    :footnote-number="14"
                />
            </sup>. The location of the basins is shown on the map below.
        </p>
        <hydrologic-variability-map
            class="hydrologic-variability-map"
            :candidate-basins="candidateBasins"
            :query-coords="reportData.lngLat"
            :query-basin="queryBasin"
            :color-scheme="colorScheme"
            :map-style="mapStyle"
            v-bind="mapColors"
            query-location-text="Query Watershed"
            @load="$emit('load')"
        />

        <!-- Only for PDF view -->
        <div
            v-if="pdf"
        >
            <hr>

            <h4 class="title is-5 header">
                Legend
            </h4>

            <chart-legend
                :items="legendItems"
                class="legend"
            />

            <hr>
        </div>

        <!-- Desktop view -->
        <div :class="{'is-hidden-touch': !pdf}">
            <p class="extra-vertical-margins">
                The watersheds shown on the map above have been identified as the most similar to the watershed
                described in this report. The table below shows key characteristics of these watersheds in relation
                to the watershed described in this report.
            </p>
            <hydrologic-variability-climate-table
                title="Query Watershed"
                :data="[reportData.hydrologicVariabilityClimateData[0]]"
                :watershed-name="reportData.overview.watershedName"
                :color-scheme="colorScheme"
                class="query-watershed"
            />
            <hr>
            <hydrologic-variability-climate-table
                title="Candidate Watershed(s)"
                :data="reportData.hydrologicVariabilityClimateData.slice(1)"
                :watershed-name="reportData.overview.watershedName"
                :color-scheme="colorScheme"
            />
            <p class="extra-btm-margin">
                The statistical distribution of flows, from the top 3 candidate basins, has been applied to the
                estimated mean monthly flows of the watershed described in this report<sup>
                    <footnote-tooltip
                        :footnote-number="15"
                    />
                </sup>. The chart and table below show the potential variability of flows using the flow duration curve
                replacement approach.
            </p>
        </div>

        <!-- Mobile view -->
        <div
            v-if="!pdf"
            class="is-hidden-desktop"
        >
            <p>
                The top candidate has been used to estimate the potential variability of flow in each month for
                the watershed described in this report<sup>
                    <footnote-tooltip
                        :footnote-number="15"
                    />
                </sup>.
            </p>
        </div>

        <hydrologic-variability-chart-subsection
            class="no-page-break"
            :report-data="reportData"
            :pdf-layout="pdf"
        />

        <div
            v-if="Object.keys(reportData.hydrologicVariability).length"
        >
            <h4 class="title header table-header">
                Tabular Data - Hydrologic Variability
            </h4>

            <div v-if="pdf">
                <hydrologic-variability-table-pdf
                    :has-candidates="true"
                    :data="reportData.hydrologicVariability"
                    :mean-data="meanData"
                />
                <hr>

                <hydrologic-variability-period-legend
                    :data="reportData.hydrologicVariability"
                />
            </div>
            <div v-else>
                <hydrologic-variability-table
                    class="is-hidden-touch"
                    :has-candidates="true"
                    :data="reportData.hydrologicVariability"
                    :mean-data="meanData"
                />

                <hydrologic-variability-table-mobile
                    class="is-hidden-desktop"
                    :has-candidates="true"
                    :data="reportData.hydrologicVariability"
                    :distance-data="reportData.hydrologicVariabilityDistanceValues"
                    :footnote-number="23"
                />
            </div>
        </div>

        <div v-else>
            No Hydrologic Variability data is available for the selected watershed.
        </div>
    </report-section>
</template>

<script>
import ChartLegend from '@foundryspatial/chart-legend';
import HydrologicVariabilityMap from '@foundryspatial/hydrologic-variability-map';
import HydrologicVariabilityTable from '@foundryspatial/hydrologic-variability-table';
import HydrologicVariabilityTableMobile from '@foundryspatial/hydrologic-variability-table-mobile';
import HydrologicVariabilityTablePdf from '@foundryspatial/hydrologic-variability-table/HydrologicVariabilityTablePdf.vue';
import sectionMixin from '@foundryspatial/report-section/mixin';

import scssVars from '@/assets/scss/_variables.scss';
import HydrologicVariabilityPeriodLegend from '@/components/HydrologicVariabilityPeriodLegend.vue';
import HydrologicVariabilityChartSubsection from '@/components/WatershedReport/HydrologicVariabilityChartSubsection.vue';
import HydrologicVariabilityClimateTable from '@/components/WatershedReport/HydrologicVariabilityClimateTable.vue';

export default {
    name: 'HydrologicVariabilitySection',

    components: {
        ChartLegend,
        HydrologicVariabilityMap,
        HydrologicVariabilityClimateTable,
        HydrologicVariabilityChartSubsection,
        HydrologicVariabilityTable,
        HydrologicVariabilityTableMobile,
        HydrologicVariabilityTablePdf,
        HydrologicVariabilityPeriodLegend,
    },

    mixins: [
        sectionMixin,
    ],

    props: {
        pdf: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data: () => ({
        // Based on tableau20 color scheme with orange removed
        // added some extra colors to bring the total to 21 which
        // is the max amount of candidates we current have
        colorScheme: [
            '#1f76b4',
            '#aec7e8',
            '#2ca02c',
            '#98df8a',
            '#d62728',
            '#ff9896',
            '#9467bd',
            '#c5b0d5',
            '#8c564b',
            '#c49c94',
            '#e377c3',
            '#f7b6d2',
            '#7f7f7f',
            '#c7c7c7',
            '#bcbd22',
            '#dbdb8d',
            '#17bdcf',
            '#8adfa6',
            '#996d51',
            '#918080',
            '#000',
        ],
    }),

    computed: {
        queryBasin() {
            return {
                polygon: this.reportData.overview.query_polygon,
            };
        },

        meanData() {
            return this.reportData.queryMonthlyHydrology.monthlyDischarge;
        },

        // color props for the minimap component
        mapColors() {
            return {
                queryMarkerColor: scssVars.colorWatershedQuery,
                queryWatershedColor: scssVars.colorWatershedQuery,
                queryWatershedOutlineColor: scssVars.colorWatershedQueryBorder,
            };
        },

        // mapbox style for the mini-map
        mapStyle() {
            return process.env.VUE_APP_MAPBOX_DEFAULT_STYLE;
        },

        candidateBasins() {
            return this.reportData.hydrologicVariabilityMiniMapGeoJson;
        },

        legendItems() {
            const legendItems = this.candidateBasins.map((basin, index) => ({
                color: this.colorScheme[index],
                borderColor: '#000',
                legendLabel: basin.candidate,
                legendType: 'box',
            }));

            return [
                {
                    color: this.mapColors.queryWatershedColor,
                    borderColor: '#000',
                    legendLabel: 'Query Watershed',
                    legendType: 'box',
                },
                ...legendItems,
            ];
        },
    },
};
</script>

<style lang="scss">
#hydrologic-variability.report-section {
    .hydrologic-variability-map {
        height: 30em;
    }

    .hydrologic-variability-climate-table {
        th {
            &:nth-child(2) div,     // Location
            &:nth-child(3) div,     // Area
            &:nth-child(4) div {    // Elevation
                justify-content: flex-end;
            }
        }

        td {
            // Watershed
            &:nth-child(1) {
                width: 120px;
            }

            // Location
            &:nth-child(2) {
                width: 60px;
                text-align: right;
            }

            // Area
            &:nth-child(3) {
                width: 60px;
                text-align: right;
            }

            // Elevation
            &:nth-child(4) {
                width: 95px;
                text-align: right;
            }
        }
    }

    @include desktop-only {
        .hydrologic-variability-tables .year-table {
            font-size: 0.6rem;

            .columns.candidate-row {
                font-size: 0.525rem;
            }
        }
    }
}

.static-report #hydrologic-variability.report-section {
    .hydrologic-variability-map {
        height: 445px;

        .hydrologic-variability-map-legend {
            display: none;
        }
    }

    hr {
        margin: 1.25em 0;
    }

    .title.is-5 {
        margin-bottom: 1em;
    }

    .legend {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        row-gap: 2px;

        .legend-item {
            margin: 0;
            display: flex;

            // 2nd column of legend
            &:nth-child(3n + 2) {
                justify-content: center;
            }

            // 3rd column of legend
            &:nth-child(3n + 3) {
                justify-content: flex-end;
            }

            .row {
                width: fit-content;

                > * {
                    margin: 0 0.3em;
                }
            }

            .visual-container {
                height: 1em;
                width: 1em;

                .color-box {
                    height: 1em;
                    border-width: 1px;
                }
            }
        }
    }

    .hydrologic-variability-climate-table {
        font-size: 10px;

        &.query-watershed {
            page-break-after: avoid;
        }

        th {
            font-size: 11px;
            font-weight: 500;

            // subheaders
            .has-text-weight-light {
                font-size: 10px;
            }

            // Area
            &:nth-child(3) div {
                text-align: right;
            }

            // Elevation
            &:nth-child(4) div {
                justify-content: flex-end;
            }

            // Precipitation
            &:nth-child(5) div {
                margin-left: 10px;
            }

            // Precipitation (snow)
            &:nth-child(6) div {
                margin-left: 10px;
            }

            // Temperature
            &:nth-child(7) div {
                margin-left: 10px;
            }
        }

        td {
            // Watershed
            &:nth-child(1) {
                width: 115px;
            }

            // Location
            &:nth-child(2) {
                width: 60px;
                text-align: right;
            }

            // Area
            &:nth-child(3) {
                width: 60px;
                text-align: right;
            }

            // Elevation
            &:nth-child(4) {
                width: 95px;
                text-align: right;
            }
        }

        .monthly-climate-sparkline {
            width: 120px;
        }
    }

    .table-header {
        font-size: 1.25em;
    }

    .hydrologic-variability-tables .year-table {
        font-size: 0.625rem;
        line-height: 0.75rem;
    }
}
</style>
